import { ReactElement } from "react";

import DropdownMenuItem from "ds/components/DropdownMenu/Item";

import TableHeadInteractiveColumnOptionsDropdown from "./Options";
import { COLUMN_CHANGED_ANALYTIC_EVENT } from "./constants";
import { TableHeadInteractiveColumnProps } from ".";
import { TableColumnsState } from "../../store/useTableStore/types";

type TableHeadInteractiveColumnActionsDropdownProps = Pick<
  TableHeadInteractiveColumnProps,
  "column" | "stickyLeft" | "analyticsPage" | "isPinningEnabled"
> &
  Pick<
    TableColumnsState,
    "columnsOrder" | "anchoredColumn" | "hideColumn" | "pinColumn" | "reorderVisibleColumns"
  >;

const TableHeadInteractiveColumnActionsDropdown = ({
  column,
  columnsOrder,
  anchoredColumn,
  stickyLeft,
  hideColumn,
  pinColumn,
  reorderVisibleColumns,
  analyticsPage,
  isPinningEnabled,
}: TableHeadInteractiveColumnActionsDropdownProps): ReactElement | null => {
  // Whatever the column's own flags say: moving or hiding it is what anchoring rules out.
  const isAnchored = column.id === anchoredColumn;

  const showHideAction = column.canHide && !isAnchored;
  const showPinAction = column.canPin;
  const showMoveActions = !!reorderVisibleColumns && column.canMove && !isAnchored;

  if (!showHideAction && !showPinAction && !showMoveActions) {
    return null;
  }

  const visibleIndex = columnsOrder.visible.indexOf(column.id);
  const isFirstColumn = visibleIndex === 0;
  const isFirstPositionAnchored = columnsOrder.visible[0] === anchoredColumn;
  const firstMovableIndex = isFirstPositionAnchored ? 1 : 0;

  const canMoveLeft = visibleIndex > firstMovableIndex;
  const canMoveRight = visibleIndex < columnsOrder.visible.length - 2;

  // Pinning a column means taking the first position, which the anchored column doesn't give up — it
  // can still pin and unpin where it is.
  const isPinAvailable = isFirstColumn || !isFirstPositionAnchored;

  const getPinActionText = () => {
    if (!isFirstColumn) return "Pin to start";
    if (stickyLeft) return "Unpin";
    return "Pin";
  };

  const getPinActionTooltip = () => {
    if (isPinningEnabled === false) return "Not enough space to pin columns";
    if (!isPinAvailable)
      return "The first column is fixed, so nothing else can be pinned to the start";
    return undefined;
  };

  return (
    <TableHeadInteractiveColumnOptionsDropdown>
      {showPinAction && (
        <DropdownMenuItem
          onAction={() => pinColumn(column.id)}
          isDisabled={isPinningEnabled === false || !isPinAvailable}
          tooltip={getPinActionTooltip()}
          analyticsPage={analyticsPage}
          analyticsTitle={COLUMN_CHANGED_ANALYTIC_EVENT}
          analyticsProps={{
            column: column.id,
            action: stickyLeft ? "unpin" : "pin",
          }}
        >
          {getPinActionText()}
        </DropdownMenuItem>
      )}

      {showHideAction && (
        <DropdownMenuItem
          onAction={() => hideColumn(column.id)}
          analyticsPage={analyticsPage}
          analyticsTitle={COLUMN_CHANGED_ANALYTIC_EVENT}
          analyticsProps={{ column: column.id, action: "hide" }}
        >
          Hide
        </DropdownMenuItem>
      )}

      {showMoveActions && (
        <>
          <DropdownMenuItem
            onAction={() => reorderVisibleColumns(column.id, "left")}
            isDisabled={!canMoveLeft}
            analyticsPage={analyticsPage}
            analyticsTitle={COLUMN_CHANGED_ANALYTIC_EVENT}
            analyticsProps={{ column: column.id, action: "move left" }}
          >
            Move left
          </DropdownMenuItem>
          <DropdownMenuItem
            onAction={() => reorderVisibleColumns(column.id, "right")}
            isDisabled={!canMoveRight}
            analyticsPage={analyticsPage}
            analyticsTitle={COLUMN_CHANGED_ANALYTIC_EVENT}
            analyticsProps={{ column: column.id, action: "move right" }}
          >
            Move right
          </DropdownMenuItem>
        </>
      )}
    </TableHeadInteractiveColumnOptionsDropdown>
  );
};

TableHeadInteractiveColumnActionsDropdown.displayName =
  "DS.Table.Head.InteractiveColumn.ActionsDropdown";

export default TableHeadInteractiveColumnActionsDropdown;
